import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Clock, Tag, TrendingUp, Users as UsersIcon, Zap, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

const BlogPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Posts', icon: Tag },
    { id: 'product', name: 'Product Updates', icon: Zap },
    { id: 'case-studies', name: 'Case Studies', icon: TrendingUp },
    { id: 'company', name: 'Company News', icon: UsersIcon }
  ];

  const blogPosts = [
    {
      id: 1,
      category: 'product',
      title: 'Introducing AI-Powered Schedule Optimization with Mai AI',
      excerpt: 'Discover how our revolutionary Mai AI engine creates perfect schedules in minutes, reducing Employee costs by up to 35% while improving employee satisfaction and work-life balance.',
      author: 'Yeshwanth K Manohar',
      authorTitle: 'Founder & CEO, Manocore Technologies',
      date: 'January 28, 2026',
      readTime: '5 min read',
      image: '/mainimage_top.png',
      featured: true,
      tags: ['AI', 'Mai AI', 'Scheduling', 'Product Update'],
      slug: 'ai-powered-schedule-optimization'
    },
    {
      id: 2,
      category: 'product',
      title: 'New Analytics Dashboard: Real-Time Insights at Your Fingertips',
      excerpt: 'Explore our completely redesigned analytics dashboard featuring real-time workforce insights, predictive forecasting, customizable reports, and AI-driven recommendations for optimal staffing.',
      author: 'Rajesh Kumar',
      authorTitle: 'Head of Product, Shiftry',
      date: 'January 25, 2026',
      readTime: '7 min read',
      image: '/Analyticspage.png',
      featured: false,
      tags: ['Analytics', 'Reports', 'Product Update', 'Dashboard'],
      slug: 'new-analytics-dashboard'
    },
    {
      id: 3,
      category: 'case-studies',
      title: 'How HealthCare Plus Saved $500K Annually with Smart Scheduling',
      excerpt: 'An in-depth case study showing how this 1,200-employee healthcare network transformed their workforce management, eliminated scheduling conflicts, reduced overtime by 40%, and saved half a million dollars in their first year with Shiftry.',
      author: 'Priya Sharma',
      authorTitle: 'Customer Success Manager, Shiftry',
      date: 'January 22, 2026',
      readTime: '8 min read',
      image: '/Admin Dashboard.png',
      featured: false,
      tags: ['Healthcare', 'ROI', 'Success Story', 'Cost Savings'],
      slug: 'healthcare-plus-case-study'
    },
    {
      id: 4,
      category: 'product',
      title: 'Face Recognition Time Tracking: The Future of Attendance',
      excerpt: 'Learn about our cutting-edge face recognition technology that eliminates buddy punching, ensures accurate time tracking, and provides seamless clock-in/out experiences while maintaining employee privacy.',
      author: 'Arjun Patel',
      authorTitle: 'Senior Engineer, Shiftry',
      date: 'January 20, 2026',
      readTime: '6 min read',
      image: '/Timesheet Page.png',
      featured: false,
      tags: ['Time Tracking', 'Biometrics', 'Product Update', 'Security'],
      slug: 'face-recognition-time-tracking'
    },
    {
      id: 5,
      category: 'company',
      title: 'Shiftry Reaches 10,000+ Active Users Milestone',
      excerpt: 'Celebrating this incredible achievement with our community! From a small startup to serving over 10,000 businesses across India, we reflect on our journey and share our vision for the future of workforce management.',
      author: 'Yeshwanth K Manohar',
      authorTitle: 'Founder & CEO, Manocore Technologies',
      date: 'January 15, 2026',
      readTime: '5 min read',
      image: '/Newsfeed.png',
      featured: false,
      tags: ['Milestone', 'Company News', 'Growth', 'Achievement'],
      slug: '10000-users-milestone'
    },
    {
      id: 6,
      category: 'case-studies',
      title: 'Restaurant Chain Cuts Employee Costs 30% Across 50 Locations',
      excerpt: 'Discover how a leading restaurant chain optimized staffing across all 50 locations using Shiftry\'s AI-powered scheduling, reduced Employee costs by 30%, improved employee retention by 45%, and increased customer satisfaction scores.',
      author: 'Meera Nair',
      authorTitle: 'Enterprise Solutions Lead, Shiftry',
      date: 'January 10, 2026',
      readTime: '7 min read',
      image: '/Schedule Page.png',
      featured: false,
      tags: ['Restaurant', 'ROI', 'Success Story', 'Multi-Location'],
      slug: 'restaurant-chain-case-study'
    },
    {
      id: 7,
      category: 'product',
      title: 'Mobile App Updates: Enhanced User Experience on iOS & Android',
      excerpt: 'We\'ve completely revamped our mobile apps with a fresh design, faster performance, offline mode, push notifications for schedule changes, and seamless integration with our face recognition system.',
      author: 'Vikram Singh',
      authorTitle: 'Mobile Team Lead, Shiftry',
      date: 'January 8, 2026',
      readTime: '4 min read',
      image: '/AI page.png',
      featured: false,
      tags: ['Mobile', 'iOS', 'Android', 'Product Update'],
      slug: 'mobile-app-updates'
    },
    {
      id: 8,
      category: 'company',
      title: 'Expanding Our Vision: Introducing Manocore HR Suite',
      excerpt: 'Beyond workforce scheduling, we\'re excited to announce the Manocore HR Suite - a comprehensive solution including payroll, leave management, performance reviews, and employee engagement tools, all integrated with Shiftry.',
      author: 'Yeshwanth K Manohar',
      authorTitle: 'Founder & CEO, Manocore Technologies',
      date: 'January 5, 2026',
      readTime: '6 min read',
      image: '/mainimage_top.png',
      featured: false,
      tags: ['HR', 'Product Suite', 'Company News', 'Innovation'],
      slug: 'manocore-hr-suite'
    }
  ];

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  return (
    <>
      <LandingNav />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Shiftry Blog
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Insights, tips, and stories about workforce management, productivity, and building better teams.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b sticky top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {selectedCategory === 'all' && featuredPost && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="grid md:grid-cols-2 gap-8">
                <div className="relative h-64 md:h-full">
                  <img 
                    src={featuredPost.image} 
                    alt={featuredPost.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {featuredPost.tags.map((tag, idx) => (
                      <span key={idx} className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {featuredPost.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex flex-col gap-2 mb-6">
                    <div className="flex items-center text-sm text-gray-500">
                      <User className="w-4 h-4 mr-2" />
                      <div>
                        <span className="font-semibold text-gray-900">{featuredPost.author}</span>
                        <span className="block text-xs text-gray-500">{featuredPost.authorTitle}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2" />
                        {featuredPost.date}
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        {featuredPost.readTime}
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate(`/blog/${featuredPost.slug}`)}
                    className="bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center w-fit"
                  >
                    Read Article
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all border border-gray-200 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {post.tags.map((tag, idx) => (
                      <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="mb-4">
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <User className="w-4 h-4 mr-2" />
                      <div>
                        <span className="font-semibold text-gray-900">{post.author}</span>
                        <span className="block text-xs text-gray-500">{post.authorTitle}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {post.date}
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {post.readTime}
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate(`/blog/${post.slug}`)}
                    className="text-blue-600 font-semibold flex items-center hover:text-blue-700 transition-colors"
                  >
                    Read More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Never Miss an Update
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Subscribe to our newsletter for weekly insights, tips, and product updates.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input 
              type="email"
              placeholder="Enter your email"
              className="px-6 py-4 rounded-xl flex-1 text-gray-900"
            />
            <button className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold hover:shadow-xl transition-all">
              Subscribe
            </button>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default BlogPage;
